import axios from 'axios';
import Cookies from 'js-cookie';
import { clearCache } from 'services/swr';
import { CsrfHeader, User } from 'services/interface';

type AuthenticatorInit = () => Promise<User>;
type GetCsrfHeader = () => Promise<CsrfHeader | undefined>;
type HandleApiKey = (apikey: string, token: string) => Promise<User>;

const SERVER_ERROR = 'A server error occurred, and your request could not be completed. Please refresh the page.';
export const APIKEY_ERROR = 'An error occured while creating your account. Please try again later.';

export const axiosService = axios.create({ baseURL: process.env.GATSBY_SITE_URL });

export const signIn = () => {
    if (typeof window !== 'undefined') {
        Cookies.set('__Host-opswat-redirect', window.location.href, { secure: true, path: '/' });
        window.location.href = '/login?commercial';
    }
};

export const signOut = async () => {
    await clearCache();
    window.location.href = '/logout';
};

export const clearRedirectCookie = () => {
    if (typeof window !== 'undefined') {
        Cookies.set('__Host-opswat-redirect', '', { secure: true, path: '/' });
    }
};

export const generateFingerPrint = async () => {
    const { default: FingerprintJS } = await import('@fingerprintjs/fingerprintjs');
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const apikey = result.visitorId;

    if (!apikey) {
        throw new Error('Failed to generate a fingerprint');
    }
    return apikey;
};

export const b64DecodeUnicode = (input: string) => {
    return decodeURIComponent(Array.prototype.map.call(atob(input), (c) => {
        return '%' + ('00' + (c.codePointAt(0) ?? 0).toString(16)).slice(-2);
    }).join(''));
};

export const handleApiKey: HandleApiKey = async (apikey, token) => {
    const url = '/api/token/apikey';
    const headers = { 'X-Csrf': token };
    const body = { apikey };

    try {
        const response = await axiosService.post(url, body, { headers });

        return response.data;
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            const responseData = error.response?.data as { code?: number; } | undefined;
            if (responseData?.code === 403004) {
                return { ipRestriction: true };
            }
            throw new Error(SERVER_ERROR);
        }

        throw error;
    }
};

export const getCsrfHeader: GetCsrfHeader = async () => {
    const url = '/api/variables';
    return axiosService.get(url, { maxRedirects: 0 })
        .then(response => {
            const token = response.data.match(/METADEFENDER_TOKEN='(.*?)'/)[1];
            const ssoData = response.data.match(/SSO='(.*?)'/)?.[1];
            const sso = ssoData && JSON.parse(b64DecodeUnicode(ssoData));

            return { token, sso };
        })
        .catch(() => {
            throw new Error('X-Csrf token not found');
        });
};

export const authenticatorInit: AuthenticatorInit = async () => {
    const csrfHeader = await getCsrfHeader();

    if (!csrfHeader) {
        throw new Error('Failed to retrieve authentication information');
    }

    let apikeyData;
    const { token, sso } = csrfHeader;
    const apikey = sso?.apikey;

    if (apikey) {
        apikeyData = await handleApiKey(apikey, token);
    }

    const user = {
        ...sso,
        ...apikeyData,
        isLoggedIn: !!apikey,
    };

    return user;
};
